/**
 * Notificações de CHAT no sino (v12) — mensagem nova do cliente.
 *
 * ## Por que uma lista em memória (e não um feed do servidor)
 * O feed do sino é `/tickets/events/recent`, que é a timeline de TICKETS —
 * mensagem de chat não está lá e não vai estar: a verdade persistida do chat
 * já tem superfície própria (o badge de não lidas na sidebar + a lista de
 * conversas, ambos servidos por `/chats` e corretos depois de qualquer F5).
 * Duplicar isso no sino exigiria montar as listas `inbox` E `mine` no layout
 * inteiro (a conversa que já é minha nunca aparece na aba Entrada), pagando
 * requisição a mais em toda invalidação, e criaria uma SEGUNDA marca de
 * "lido" competindo com o read do chat.
 *
 * Então o sino mostra exatamente o que ele é: o que CHEGOU enquanto esta aba
 * esteve aberta — a mesma coisa que tocou o som. Fecha a aba, some a lista; o
 * que continua lá é o badge do chat, que nunca dependeu disto. Uma linha por
 * CHAT (mensagens seguintes do mesmo contato só incrementam o contador), no
 * máximo MAX_ENTRIES.
 */
import { useSyncExternalStore } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { ChatDetail, ChatListItem } from '@erp/shared';
import { chatDetailKey, chatListsKey, chatMessagesKey } from '@/features/chats/chats-queries';

const MAX_ENTRIES = 20;

export interface ChatAlertEntry {
  chatId: number;
  /** nome do solicitante quando dava para saber sem rede; null = genérico */
  requestorName: string | null;
  /** mensagens deste chat acumuladas nesta sessão da aba */
  count: number;
  /** ms — quando a última mensagem chegou (compara com a marca d'água do sino) */
  at: number;
}

let entries: ChatAlertEntry[] = [];
const listeners = new Set<() => void>();

function emit(next: ChatAlertEntry[]): void {
  entries = next;
  for (const l of listeners) l();
}

/** Registra a chegada e devolve a linha resultante (para o toast/notificação). */
export function pushChatAlert(input: {
  chatId: number;
  requestorName: string | null;
  at?: number;
}): ChatAlertEntry {
  const previous = entries.find((e) => e.chatId === input.chatId);
  const entry: ChatAlertEntry = {
    chatId: input.chatId,
    // nome novo ganha do genérico antigo (a lista pode ter chegado no meio)
    requestorName: input.requestorName ?? previous?.requestorName ?? null,
    count: (previous?.count ?? 0) + 1,
    at: input.at ?? Date.now(),
  };
  emit([entry, ...entries.filter((e) => e.chatId !== input.chatId)].slice(0, MAX_ENTRIES));
  return entry;
}

/** Some com a linha (o usuário abriu o chat pelo sino). */
export function dismissChatAlert(chatId: number): void {
  emit(entries.filter((e) => e.chatId !== chatId));
}

export function useChatAlerts(): ChatAlertEntry[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => entries,
    () => entries
  );
}

/**
 * "O usuário JÁ está olhando ESTE chat?" — sem inventar um registro de seleção
 * paralelo: a seleção da /chats é estado local (invisível da topbar), mas
 * quem tem a conversa aberta é, por construção, quem monta
 * `useChatMessages(chatId)` — a janela do /chats ou a aba Conversas do
 * ticket. Zero observers naquela key = ninguém com aquele chat na tela.
 *
 * Aba escondida NÃO conta como olhando: com o ERP em outra aba/minimizado o
 * atendente não está lendo nada — é justamente quando o som importa.
 */
export function isChatWindowOpen(qc: QueryClient, chatId: number): boolean {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
  const query = qc.getQueryCache().find({ queryKey: chatMessagesKey(chatId) });
  return (query?.getObserversCount() ?? 0) > 0;
}

/**
 * Nome do solicitante SEM rede: o detalhe do chat (se aberto alguma vez) ou
 * qualquer variante da lista de conversas já no cache — o badge da sidebar
 * mantém a aba Entrada montada, então o caso comum (chat no inbox) acerta.
 * null ⇒ o chamador usa texto genérico.
 */
export function lookupChatRequestorName(qc: QueryClient, chatId: number): string | null {
  const detail = qc.getQueryData<ChatDetail>(chatDetailKey(chatId));
  if (detail?.requestorName) return detail.requestorName;
  const lists = qc.getQueriesData<ChatListItem[]>({ queryKey: chatListsKey });
  for (const [, list] of lists) {
    const found = list?.find((c) => c.id === chatId);
    if (found?.requestorName) return found.requestorName;
  }
  return null;
}
